import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLeaveTypeMutations } from '../api'
import type { LeaveTypeDetailView, TierView } from '../types'

type Draft = { seq: number; fromDay: number; toDay: string; payPercent: string; label: string }

function toDraft(tiers: TierView[]): Draft[] {
  return tiers.map((tier) => ({
    seq: tier.seq,
    fromDay: tier.fromDay,
    toDay: tier.toDay == null ? '' : String(tier.toDay),
    payPercent: String(tier.payPercent),
    label: tier.label ?? '',
  }))
}

/**
 * The pay bands of a tiered entitlement.
 *
 * <p>UAE sick leave is fifteen days at full wage, thirty at half and forty-five unpaid —
 * Art. 31 — and maternity is forty-five then fifteen. Bands have to be contiguous from day
 * one: a gap leaves a day with no defined pay rate, which nobody notices until a payslip is
 * wrong. The editor keeps the boundaries in step as you type, and the backend refuses the
 * save if they ever are not.
 */
export function LeaveTypeTierEditor({ type }: { type: LeaveTypeDetailView }) {
  const { replaceTiers } = useLeaveTypeMutations()
  const [drafts, setDrafts] = useState<Draft[]>(toDraft(type.tiers))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  /** Each band starts the day after the previous one ends, so a gap cannot be typed by accident. */
  function relink(rows: Draft[]): Draft[] {
    let nextFrom = 1
    return rows.map((row, index) => {
      const linked = { ...row, seq: index + 1, fromDay: nextFrom }
      const end = Number(linked.toDay)
      nextFrom = Number.isFinite(end) && end >= linked.fromDay ? end + 1 : linked.fromDay
      return linked
    })
  }

  /** Kept out of the JSX so the click handler itself returns void, not a promise. */
  async function save() {
    setError(null)
    setSaved(false)
    try {
      await replaceTiers.mutateAsync({
        id: type.id,
        tiers: drafts.map((row) => ({
          seq: row.seq,
          fromDay: row.fromDay,
          // An empty last box means the final band is open-ended, which is a different
          // thing from a band that ends on day zero.
          toDay: row.toDay === '' ? null : Number(row.toDay),
          payPercent: Number(row.payPercent),
          label: row.label || null,
        })),
      })
      setSaved(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Those bands could not be saved')
    }
  }

  function update(index: number, patch: Partial<Draft>) {
    setSaved(false)
    setDrafts((rows) => relink(rows.map((row, i) => (i === index ? { ...row, ...patch } : row))))
  }

  return (
    <div className="space-y-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <th scope="col" className="pb-2 pr-3 font-medium">From day</th>
            <th scope="col" className="pb-2 pr-3 font-medium">To day</th>
            <th scope="col" className="pb-2 pr-3 font-medium">Pay %</th>
            <th scope="col" className="pb-2 pr-3 font-medium">Label</th>
            <th scope="col" className="pb-2"><span className="sr-only">Remove</span></th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((row, index) => (
            <tr key={row.seq}>
              <td className="py-1 pr-3">
                <Input
                  aria-label={`Band ${row.seq} first day`}
                  value={row.fromDay}
                  readOnly
                  className="w-20 bg-[var(--color-page)]"
                />
              </td>
              <td className="py-1 pr-3">
                <Input
                  aria-label={`Band ${row.seq} last day`}
                  type="number"
                  min={row.fromDay}
                  value={row.toDay}
                  onChange={(event) => update(index, { toDay: event.target.value })}
                  placeholder={index === drafts.length - 1 ? 'no limit' : ''}
                  className="w-24"
                />
              </td>
              <td className="py-1 pr-3">
                <Input
                  aria-label={`Band ${row.seq} pay percent`}
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={row.payPercent}
                  onChange={(event) => update(index, { payPercent: event.target.value })}
                  className="w-24"
                />
              </td>
              <td className="py-1 pr-3">
                <Input
                  aria-label={`Band ${row.seq} label`}
                  value={row.label}
                  onChange={(event) => update(index, { label: event.target.value })}
                  placeholder="Full wage"
                />
              </td>
              <td className="py-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove band ${row.seq}`}
                  onClick={() => {
                    setSaved(false)
                    setDrafts((rows) => relink(rows.filter((_, i) => i !== index)))
                  }}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setSaved(false)
            setDrafts((rows) =>
              relink([...rows, { seq: rows.length + 1, fromDay: 1, toDay: '', payPercent: '0', label: '' }]),
            )
          }}
        >
          <Plus className="size-4" aria-hidden="true" />
          Add a band
        </Button>

        <Button
          type="button"
          size="sm"
          disabled={replaceTiers.isPending}
          onClick={() => void save()}
        >
          {replaceTiers.isPending ? 'Saving…' : 'Save bands'}
        </Button>

        {saved && (
          <span role="status" className="text-sm text-[var(--color-success)]">
            Saved.
          </span>
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  )
}
